import { useDeferredValue, useEffect, useMemo, useState } from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import {
  formatFleetMasterTimestamp,
  useFleetMasterDataWorkspaceContext,
} from "@/features/operator/fleet/fleet-master-data-shared"
import {
  type FleetOnboardingSearch,
  FLEET_VERTICAL_ASSET_LABELS,
  FLEET_VERTICAL_ROUTE_LABELS,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  apiClient,
  type FleetAssetRecord,
  type FleetOperatingUnitRecord,
  isApiError,
} from "@/lib/api/client"

interface FleetAssetsPageProps {
  search?: FleetOnboardingSearch
}

interface FleetAssetFormState {
  assetNumber: string
  assetType: string
  notes: string
  operatingUnitId: string
  routeOrZone: string
  status: string
}

const DEFAULT_FORM_STATE: FleetAssetFormState = {
  assetNumber: "",
  assetType: "truck",
  notes: "",
  operatingUnitId: "",
  routeOrZone: "",
  status: "active",
}

function toFormState(record?: FleetAssetRecord | null): FleetAssetFormState {
  if (!record) {
    return DEFAULT_FORM_STATE
  }

  return {
    assetNumber: record.assetNumber,
    assetType: record.assetType,
    notes: record.notes || "",
    operatingUnitId: record.operatingUnitId || "",
    routeOrZone: record.routeOrZone || "",
    status: record.status,
  }
}

export function FleetAssetsPage({ search }: FleetAssetsPageProps) {
  const { organizationId, organizationLabel, sessionError, sessionStatus, vertical } =
    useFleetMasterDataWorkspaceContext(search)
  const [assets, setAssets] = useState<FleetAssetRecord[]>([])
  const [units, setUnits] = useState<FleetOperatingUnitRecord[]>([])
  const [formState, setFormState] = useState<FleetAssetFormState>({
    ...DEFAULT_FORM_STATE,
    assetType: vertical === "intercity_bus" ? "bus" : vertical === "two_wheeler_delivery" ? "two_wheeler" : "truck",
  })
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeletingAssetId, setIsDeletingAssetId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchText, setSearchText] = useState("")
  const deferredSearchText = useDeferredValue(searchText)
  const assetLabel = FLEET_VERTICAL_ASSET_LABELS[vertical]
  const routeLabel = FLEET_VERTICAL_ROUTE_LABELS[vertical]

  useEffect(() => {
    setFormState((previous) => ({
      ...previous,
      assetType:
        vertical === "intercity_bus"
          ? "bus"
          : vertical === "two_wheeler_delivery"
            ? "two_wheeler"
            : "truck",
    }))
  }, [vertical])

  useEffect(() => {
    if (!organizationId) {
      setAssets([])
      setUnits([])
      return
    }

    let isActive = true
    setIsLoading(true)
    setError(null)

    void Promise.all([
      apiClient.getFleetAssets({ organizationId, vertical }),
      apiClient.getFleetOperatingUnits({ organizationId }),
    ])
      .then(([assetRecords, unitRecords]) => {
        if (!isActive) {
          return
        }

        setAssets(assetRecords)
        setUnits(unitRecords)
      })
      .catch((loadError) => {
        if (!isActive) {
          return
        }

        setError(
          isApiError(loadError)
            ? loadError.message
            : loadError instanceof Error
              ? loadError.message
              : "Failed to load fleet assets."
        )
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [organizationId, vertical])

  const filteredAssets = useMemo(() => {
    const query = deferredSearchText.trim().toLowerCase()
    if (!query) {
      return assets
    }

    return assets.filter((asset) =>
      [
        asset.assetNumber,
        asset.assetType,
        asset.routeOrZone || "",
        asset.status,
        asset.operatingUnit?.code || "",
        asset.operatingUnit?.name || "",
      ].some((value) => value.toLowerCase().includes(query))
    )
  }, [assets, deferredSearchText])

  const summary = useMemo(() => {
    return {
      activeAssets: assets.filter((asset) => asset.status === "active").length,
      assignedAssets: assets.filter((asset) => asset.assignmentCount > 0).length,
      coveredUnits: new Set(
        assets.map((asset) => asset.operatingUnitId).filter(Boolean)
      ).size,
      totalAssignments: assets.reduce((sum, asset) => sum + asset.assignmentCount, 0),
    }
  }, [assets])

  const unitOptions = useMemo(() => {
    return units.filter((unit) => unit.vertical === vertical).length > 0
      ? units.filter((unit) => unit.vertical === vertical)
      : units
  }, [units, vertical])

  function resetForm() {
    setEditingAssetId(null)
    setFormState({
      ...DEFAULT_FORM_STATE,
      assetType:
        vertical === "intercity_bus"
          ? "bus"
          : vertical === "two_wheeler_delivery"
            ? "two_wheeler"
            : "truck",
    })
  }

  function beginEditing(asset: FleetAssetRecord) {
    setEditingAssetId(asset.id)
    setFormState(toFormState(asset))
    setNotice(null)
    setError(null)
  }

  async function saveAsset() {
    if (!organizationId) {
      setError("No Fleet organization is selected for asset management.")
      return
    }

    setIsSaving(true)
    setNotice(null)
    setError(null)

    try {
      const payload = {
        ...formState,
        assetNumber: formState.assetNumber.toUpperCase(),
        organizationId,
        operatingUnitId: formState.operatingUnitId || null,
        vertical,
      }

      const savedAsset = editingAssetId
        ? await apiClient.updateFleetAsset(editingAssetId, payload)
        : await apiClient.createFleetAsset(payload)

      setAssets((previous) =>
        editingAssetId
          ? previous.map((asset) => (asset.id === savedAsset.id ? savedAsset : asset))
          : [savedAsset, ...previous]
      )
      setNotice(
        editingAssetId
          ? `Updated ${assetLabel.toLowerCase()} ${savedAsset.assetNumber}.`
          : `Created ${assetLabel.toLowerCase()} ${savedAsset.assetNumber}.`
      )
      resetForm()
    } catch (saveError) {
      setError(
        isApiError(saveError)
          ? saveError.message
          : saveError instanceof Error
            ? saveError.message
            : "Failed to save the fleet asset."
      )
    } finally {
      setIsSaving(false)
    }
  }

  async function deleteAsset(asset: FleetAssetRecord) {
    if (!organizationId) {
      return
    }

    setIsDeletingAssetId(asset.id)
    setNotice(null)
    setError(null)

    try {
      await apiClient.deleteFleetAsset(asset.id, organizationId)
      setAssets((previous) => previous.filter((record) => record.id !== asset.id))
      if (editingAssetId === asset.id) {
        resetForm()
      }
      setNotice(`Deleted ${assetLabel.toLowerCase()} ${asset.assetNumber}.`)
    } catch (deleteError) {
      setError(
        isApiError(deleteError)
          ? deleteError.message
          : deleteError instanceof Error
            ? deleteError.message
            : "Failed to delete the fleet asset."
      )
    } finally {
      setIsDeletingAssetId(null)
    }
  }

  return (
    <FleetB2BPageShell
      activeSection="assets"
      title="Fleet assets"
      description="Track trucks, buses, and two-wheelers as first-class enterprise assets before any sticker kit is issued."
      highlights={[
        "Asset number is mandatory and remains the enterprise anchor for kit issuance.",
        "Vertical-specific route, zone, and service metadata now lives on persisted assets.",
        "Assignments are no longer flattened into asset rows, but assignment coverage is visible here.",
      ]}
    >
      <div className="grid gap-5">
        <OperatorStatusMessage>
          Active Fleet organization: <strong>{organizationLabel}</strong>.
        </OperatorStatusMessage>

        {sessionStatus === "loading" ? (
          <OperatorStatusMessage>Loading Fleet organization context…</OperatorStatusMessage>
        ) : null}
        {sessionError ? <OperatorStatusMessage tone="danger">{sessionError}</OperatorStatusMessage> : null}
        {error ? <OperatorStatusMessage tone="danger">{error}</OperatorStatusMessage> : null}
        {notice ? <OperatorStatusMessage>{notice}</OperatorStatusMessage> : null}

        {!organizationId ? (
          <OperatorPanel
            title="Organization context required"
            description="This workspace needs an organization scope from the current operator session or the latest Fleet onboarding persistence."
          >
            <OperatorEmptyState
              title="No Fleet organization is currently selected."
              message="Finish a Fleet onboarding review or open this workspace from an organization-bound operator session to manage persisted assets."
            />
          </OperatorPanel>
        ) : (
          <>
            <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <OperatorMetricCard
                label={`${assetLabel} assets`}
                value={assets.length}
                detail="Persisted assets within the current Fleet organization."
              />
              <OperatorMetricCard
                label="Active assets"
                value={summary.activeAssets}
                detail="Assets still marked active for issuance and assignment."
              />
              <OperatorMetricCard
                label="Assigned assets"
                value={summary.assignedAssets}
                detail="Assets that already have a persisted assignment record."
              />
              <OperatorMetricCard
                label="Covered units"
                value={summary.coveredUnits}
                detail="Operating units currently referenced by persisted assets."
              />
            </section>

            <section className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
              <OperatorPanel
                title={editingAssetId ? `Edit ${assetLabel.toLowerCase()}` : `Create ${assetLabel.toLowerCase()}`}
                description="Use this form to maintain the authoritative persisted asset roster."
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">{assetLabel} number</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.assetNumber}
                      onChange={(event) =>
                        setFormState((previous) => ({
                          ...previous,
                          assetNumber: event.target.value.toUpperCase(),
                        }))
                      }
                      placeholder={vertical === "two_wheeler_delivery" ? "KA01ZX1234" : "KA01AB1234"}
                    />
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Asset type</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.assetType}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, assetType: event.target.value }))
                      }
                    >
                      <option value="truck">Truck</option>
                      <option value="bus">Bus</option>
                      <option value="two_wheeler">Two-wheeler</option>
                      <option value="van">Van</option>
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Operating unit</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.operatingUnitId}
                      onChange={(event) =>
                        setFormState((previous) => ({
                          ...previous,
                          operatingUnitId: event.target.value,
                        }))
                      }
                    >
                      <option value="">Unassigned operating unit</option>
                      {unitOptions.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.code} • {unit.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Status</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.status}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, status: event.target.value }))
                      }
                    >
                      <option value="active">Active</option>
                      <option value="reserve">Reserve</option>
                      <option value="inactive">Inactive</option>
                      <option value="archived">Archived</option>
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm md:col-span-2">
                    <span className="text-muted-foreground">{routeLabel}</span>
                    <input
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.routeOrZone}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, routeOrZone: event.target.value }))
                      }
                      placeholder={vertical === "intercity_bus" ? "Bengaluru -> Hyderabad" : "South corridor lane 4"}
                    />
                  </label>
                  <label className="grid gap-2 text-sm md:col-span-2">
                    <span className="text-muted-foreground">Notes</span>
                    <textarea
                      className="min-h-24 rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.notes}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, notes: event.target.value }))
                      }
                      placeholder="Optional persisted asset notes"
                    />
                  </label>
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => void saveAsset()}
                    disabled={isSaving}
                    className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                  >
                    {isSaving ? "Saving…" : editingAssetId ? `Save ${assetLabel.toLowerCase()}` : `Create ${assetLabel.toLowerCase()}`}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                  >
                    Clear form
                  </button>
                </div>
              </OperatorPanel>

              <OperatorPanel
                title="Persisted asset roster"
                description="Review persisted assets, search by route or unit, and manage the roster without returning to onboarding."
              >
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <label className="min-w-[18rem] flex-1 text-sm">
                    <span className="mb-2 block text-muted-foreground">Search assets</span>
                    <input
                      className="w-full rounded-2xl border border-border bg-background px-3 py-2"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      placeholder="Search by asset number, route, unit, or status"
                    />
                  </label>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-3 text-sm text-muted-foreground">
                    Assignment links: <strong>{summary.totalAssignments}</strong>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-sm text-muted-foreground">Loading persisted assets…</div>
                ) : filteredAssets.length === 0 ? (
                  <OperatorEmptyState
                    title="No assets match the current view."
                    message="Create the first persisted asset or broaden the search to see existing records."
                  />
                ) : (
                  <div className="grid gap-3">
                    {filteredAssets.map((asset) => (
                      <div
                        key={asset.id}
                        className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="text-lg font-semibold">{asset.assetNumber}</div>
                            <div className="mt-1 text-sm text-muted-foreground">
                              {asset.assetType} • {asset.operatingUnit?.code || "No operating unit"}
                              {asset.routeOrZone ? ` • ${asset.routeOrZone}` : ""}
                            </div>
                            <div className="mt-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                              {asset.status}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => beginEditing(asset)}
                              className="rounded-2xl border border-border px-3 py-2 text-sm font-medium"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => void deleteAsset(asset)}
                              disabled={isDeletingAssetId === asset.id}
                              className="rounded-2xl border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive disabled:opacity-60"
                            >
                              {isDeletingAssetId === asset.id ? "Deleting…" : "Delete"}
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <OperatorMetricCard
                            label="Operating unit"
                            value={asset.operatingUnit?.name || "Unassigned"}
                            detail={asset.operatingUnit?.city || "No operating unit linked yet."}
                          />
                          <OperatorMetricCard
                            label="Assignment links"
                            value={asset.assignmentCount}
                            detail="Persisted assignment records attached to this asset."
                          />
                          <OperatorMetricCard
                            label="Route / zone"
                            value={asset.routeOrZone || "Not set"}
                            detail={asset.notes || "No persisted notes captured."}
                          />
                          <OperatorMetricCard
                            label="Updated"
                            value={formatFleetMasterTimestamp(asset.updatedAt)}
                            detail={`Created ${formatFleetMasterTimestamp(asset.createdAt)}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </OperatorPanel>
            </section>
          </>
        )}
      </div>
    </FleetB2BPageShell>
  )
}
